import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { useAuth } from '../../contexts/AuthContext';
import { appAnalyticsAPI } from '../../services/api';
import { AppFilters, AppNav, AppStyles, FilterBar, NoPlanNotice, defaultFilters, filterParams, fmtDuration, fmtNum } from './common';

const COMPARE_COLORS = ['#6366f1', '#f59e0b', '#10b981', '#ef4444', '#0ea5e9'];

const AppFunnelPage: React.FC = () => {
  const { currentAsset } = useAuth();
  const assetId = currentAsset?.id;
  const [filters, setFilters] = useState<AppFilters>(defaultFilters(30));
  const [funnel, setFunnel] = useState('lifecycle');
  const [windowDays, setWindowDays] = useState(7);
  const [compare, setCompare] = useState<string[]>([]);
  const [versions, setVersions] = useState<string[]>([]);
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!assetId) return;
    appAnalyticsAPI.getFilters(assetId).then(r => setVersions(r.data?.data?.app_versions || [])).catch(() => {});
  }, [assetId]);

  useEffect(() => {
    if (!assetId) return;
    setLoading(true);
    appAnalyticsAPI.getFunnel({ ...filterParams(assetId, filters), funnel, window_days: windowDays, ...(compare.length ? { compare_versions: compare.join(',') } : {}) })
      .then(r => setData(r.data?.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [assetId, filters, funnel, windowDays, compare]);

  const steps: any[] = data?.steps || [];
  const top = steps[0]?.users || 0;
  const biggestDrop = steps.slice(1).reduce((m: any, s: any) => (!m || s.drop_off > m.drop_off ? s : m), null);

  return (
    <DashboardLayout>
      <AppStyles />
      <div className="container-fluid py-4">
        <h4 className="fw-bold mb-0"><i className="bi bi-funnel text-primary me-2"></i>Lifecycle funnel</h4>
        <p className="text-muted small mb-2">Ordered: a user only counts for a step if they did it <em>after</em> the previous step, within the conversion window.</p>
        <AppNav />
        {data && !data.has_plan ? <NoPlanNotice /> : (
          <>
            <FilterBar assetId={assetId} value={filters} onChange={setFilters}>
              <select className="form-select form-select-sm" style={{ width: 190 }} value={funnel} onChange={e => setFunnel(e.target.value)} aria-label="Funnel">
                {(data?.funnels || [{ key: 'lifecycle', label: 'User lifecycle' }]).map((f: any) => <option key={f.key} value={f.key}>{f.label}</option>)}
              </select>
              <select className="form-select form-select-sm" style={{ width: 150 }} value={windowDays} onChange={e => setWindowDays(Number(e.target.value))} aria-label="Conversion window">
                {[1, 3, 7, 14, 30, 90].map(d => <option key={d} value={d}>{d}-day window</option>)}
              </select>
            </FilterBar>

            {loading && !data ? <div className="text-center py-5"><div className="spinner-border text-primary"></div></div> : (
              <div className="row g-3">
                <div className="col-xl-8">
                  <div className="card border-0 shadow-sm">
                    <div className="card-body">
                      {steps.length === 0 ? (
                        <div className="text-muted text-center py-4">This funnel has no steps. <Link to="/app-analytics/setup">Edit the tracking plan →</Link></div>
                      ) : steps.map((s: any, i: number) => (
                        <div key={s.step_key} className="mb-3">
                          {i > 0 && (
                            <div className="d-flex align-items-center gap-3 small text-muted ms-2 mb-1">
                              <i className="bi bi-arrow-down"></i>
                              <span className={s.conversion_from_prev < 50 ? 'text-danger' : 'text-success'}>{s.conversion_from_prev}% continued</span>
                              {s.drop_off > 0 && <span>{fmtNum(s.drop_off)} dropped off</span>}
                              {s.median_sec_from_prev !== null && <span><i className="bi bi-stopwatch me-1"></i>median {fmtDuration(s.median_sec_from_prev)}</span>}
                            </div>
                          )}
                          <div className="d-flex justify-content-between small mb-1">
                            <span className="fw-semibold">
                              <span className="badge rounded-pill bg-light text-dark border me-2">{i + 1}</span>{s.display_name}
                              <code className="ms-2 small text-muted">{s.step_key}</code>
                              {s.is_conversion && <span className="badge bg-success-subtle text-success ms-2">conversion</span>}
                            </span>
                            <span><strong>{fmtNum(s.users)}</strong> users · {s.conversion_from_start}%</span>
                          </div>
                          <div className="ca-funnel-track">
                            <div className="ca-funnel-bar" style={{ width: `${top ? (s.users / top) * 100 : 0}%`, background: s.is_conversion ? 'linear-gradient(90deg,#10b981,#34d399)' : undefined }}></div>
                          </div>
                          {(data?.comparison || []).length > 0 && (
                            <div className="mt-1">
                              {data.comparison.map((c: any, k: number) => {
                                const cs = c.steps[i];
                                const ct = c.steps[0]?.users || 0;
                                return (
                                  <div key={c.app_version} className="d-flex align-items-center gap-2 small">
                                    <span style={{ width: 70 }} className="text-muted">v{c.app_version}</span>
                                    <div className="flex-grow-1 ca-funnel-track" style={{ height: 8 }}>
                                      <div style={{ height: 8, borderRadius: 4, width: `${ct ? (cs.users / ct) * 100 : 0}%`, background: COMPARE_COLORS[k % COMPARE_COLORS.length] }}></div>
                                    </div>
                                    <span style={{ width: 90 }} className="text-end">{fmtNum(cs.users)} · {cs.conversion_from_start}%</span>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="col-xl-4">
                  <div className="card border-0 shadow-sm mb-3">
                    <div className="card-body">
                      <div className="text-muted small">Overall conversion</div>
                      <div className="display-6 fw-bold">{data?.overall_conversion ?? 0}%</div>
                      <div className="small text-muted">{fmtNum(top)} users entered · {fmtNum(steps[steps.length - 1]?.users || 0)} completed</div>
                      {biggestDrop && biggestDrop.drop_off > 0 && (
                        <div className="alert alert-warning small mt-3 mb-0">
                          <i className="bi bi-exclamation-triangle me-1"></i>
                          Biggest drop-off: <strong>{fmtNum(biggestDrop.drop_off)}</strong> users didn't reach <strong>{biggestDrop.display_name}</strong>.{' '}
                          <Link to={`/app-analytics/users?reached=${encodeURIComponent(steps[steps.indexOf(biggestDrop) - 1]?.step_key || '')}`}>See those users →</Link>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="card border-0 shadow-sm">
                    <div className="card-body">
                      <h6 className="fw-bold">Compare app versions</h6>
                      {versions.length === 0 && <div className="text-muted small">No versions reported yet.</div>}
                      {versions.slice(0, 12).map(v => (
                        <div className="form-check" key={v}>
                          <input className="form-check-input" type="checkbox" id={`cv-${v}`} checked={compare.includes(v)}
                            onChange={() => setCompare(c => (c.includes(v) ? c.filter(x => x !== v) : [...c, v].slice(0, 5)))} />
                          <label className="form-check-label small" htmlFor={`cv-${v}`}>v{v}</label>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

export default AppFunnelPage;
